// This file is auto-generated by @hey-api/openapi-ts

import type {
  Client,
  ClientMeta,
  Options as Options2,
  RequestResult,
  TDataShape,
} from './client';
import { client } from './client.gen';
import type {
  CancelJobData,
  CancelJobErrors,
  CancelJobResponses,
  CheckHealthData,
  CheckHealthErrors,
  CheckHealthResponses,
  CompleteTrajectoryUploadData,
  CompleteTrajectoryUploadErrors,
  CompleteTrajectoryUploadResponses,
  CompleteUploadData,
  CompleteUploadErrors,
  CompleteUploadResponses,
  CreateDatasetData,
  CreateDatasetErrors,
  CreateDatasetResponses,
  CreateImageData,
  CreateImageErrors,
  CreateImageResponses,
  CreateImagesData,
  CreateImagesErrors,
  CreateImagesResponses,
  CreateTrajectoryData,
  CreateTrajectoryErrors,
  CreateTrajectoryResponses,
  DeleteDatasetData,
  DeleteDatasetErrors,
  DeleteDatasetResponses,
  DeleteImageData,
  DeleteImageErrors,
  DeleteImageResponses,
  DeleteImagesData,
  DeleteImagesErrors,
  DeleteImagesResponses,
  DeleteTrajectoryData,
  DeleteTrajectoryErrors,
  DeleteTrajectoryResponses,
  GetDatasetData,
  GetDatasetErrors,
  GetDatasetResponses,
  GetImageData,
  GetImageErrors,
  GetImageResponses,
  GetImageUrlsData,
  GetImageUrlsErrors,
  GetImageUrlsResponses,
  GetJobData,
  GetJobErrors,
  GetJobResponses,
  GetTrajectoryData,
  GetTrajectoryErrors,
  GetTrajectoryResponses,
  GetTrajectoryResultsData,
  GetTrajectoryResultsErrors,
  GetTrajectoryResultsResponses,
  IngestDatasetData,
  IngestDatasetErrors,
  IngestDatasetResponses,
  IngestImageData,
  IngestImageErrors,
  IngestImageResponses,
  ListDatasetsData,
  ListDatasetsErrors,
  ListDatasetsResponses,
  ListImagesData,
  ListImagesErrors,
  ListImagesResponses,
  ListJobsData,
  ListJobsErrors,
  ListJobsResponses,
  PatchDatasetData,
  PatchDatasetErrors,
  PatchDatasetResponses,
  QueryDatasetsData,
  QueryDatasetsErrors,
  QueryDatasetsResponses,
  StartTrajectoryProcessingData,
  StartTrajectoryProcessingErrors,
  StartTrajectoryProcessingResponses,
  TilesetCompleteUploadData,
  TilesetCompleteUploadErrors,
  TilesetCompleteUploadResponses,
  TilesetCreateData,
  TilesetCreateErrors,
  TilesetCreateResponses,
} from './types.gen';

export type Options<
  TData extends TDataShape = TDataShape,
  ThrowOnError extends boolean = boolean,
  TResponse = unknown,
> = Options2<TData, ThrowOnError, TResponse> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client;
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: keyof ClientMeta extends never ? Record<string, unknown> : ClientMeta;
};

/**
 * List all Datasets associated with the tenant
 *
 * ### List Datasets
 *
 * This endpoint allows you to list all Datasets associated with the tenant.
 *
 * ### Notes
 *
 * - The list is paginated with the optional page parameters.
 * - The list is sorted by creation date in descending order.
 */
export const listDatasets = <ThrowOnError extends boolean = false>(
  options: Options<ListDatasetsData, ThrowOnError>
): RequestResult<ListDatasetsResponses, ListDatasetsErrors, ThrowOnError> =>
  (options.client ?? client).get<
    ListDatasetsResponses,
    ListDatasetsErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets',
    ...options,
  });

/**
 * Create a Dataset
 *
 * ### Create a Dataset
 *
 * This endpoint allows you to create a Dataset.
 *
 * ### Notes
 *
 * - The name does not have to be unique.
 */
export const createDataset = <ThrowOnError extends boolean = false>(
  options: Options<CreateDatasetData, ThrowOnError>
): RequestResult<CreateDatasetResponses, CreateDatasetErrors, ThrowOnError> =>
  (options.client ?? client).post<
    CreateDatasetResponses,
    CreateDatasetErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Delete a Dataset
 *
 * ### Delete a Dataset
 *
 * This endpoint allows you to delete a Dataset.
 *
 * ### Notes
 *
 * - Deleting a Dataset will permanently remove it and all images within it.
 *
 * ### Permissions
 *
 * - Only users with the **Admin** role in the tenant can delete a Dataset.
 */
export const deleteDataset = <ThrowOnError extends boolean = false>(
  options: Options<DeleteDatasetData, ThrowOnError>
): RequestResult<DeleteDatasetResponses, DeleteDatasetErrors, ThrowOnError> =>
  (options.client ?? client).delete<
    DeleteDatasetResponses,
    DeleteDatasetErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}',
    ...options,
  });

/**
 * Get a Dataset with the given id
 *
 * ### Get a Dataset
 *
 * This endpoint allows you to get a Dataset by its id.
 */
export const getDataset = <ThrowOnError extends boolean = false>(
  options: Options<GetDatasetData, ThrowOnError>
): RequestResult<GetDatasetResponses, GetDatasetErrors, ThrowOnError> =>
  (options.client ?? client).get<
    GetDatasetResponses,
    GetDatasetErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}',
    ...options,
  });

/**
 * Partially update dataset metadata. Only non-null fields in the body are merged.
 *
 * ### Patch dataset
 *
 * The request body is a `PatchDataset` object. Each top-level property is optional; omitted properties
 * do not change the stored dataset. When a nested object (`coordinateSystem` or `orientationSystem`)
 * is included, only its non-null fields are merged into the stored value.
 *
 * ### Field behaviour
 *
 * Enum values in the request body are case-sensitive and use the names shown in parentheses below
 * (for example `Geographic`, not `geographic` or `gEO`).
 *
 * - `name` — When a non-empty value is sent, it replaces the stored name. When omitted or JSON `null`,
 * the stored name is left unchanged.
 * - `coordinateSystem` — See coordinate-system field rules below. When omitted, the stored coordinate system
 * is left unchanged.
 * - `orientationSystem` — See orientation-system field rules below. When omitted, the stored orientation
 * system is left unchanged.
 *
 * #### Coordinate system
 *
 * - `crsType` — When a value is sent, it replaces the stored CRS type. When omitted or null, the stored
 * CRS type is left unchanged. (allowed: `Geographic`, `ECEF`, `Projected`, `Relative`)
 * - `distanceUnit` — When a value is sent, it replaces the stored distance unit. When omitted or null,
 * the stored unit is left unchanged. (allowed: `USSurveyFoot`, `InternationalFoot`, `Meter`)
 * - `tglCRSID` — When the property is present with a non-null JSON value (including an empty string), it
 * replaces the stored Trimble CSIB string. When the property is omitted or JSON `null`, the stored value
 * is left unchanged.
 *
 * #### Orientation system
 *
 * - `upAxis` — When a value is sent, it replaces the stored up axis. When omitted or null, unchanged.
 * (allowed: `X`, `Y`, `Z`, `NegX`, `NegY`, `NegZ`)
 * - `forwardAxis` — When a value is sent, it replaces the stored forward axis. When omitted or null,
 * unchanged. (allowed: `X`, `Y`, `Z`, `NegX`, `NegY`, `NegZ`)
 * - `handedness` — When a value is sent, it replaces the stored handedness. When omitted or null,
 * unchanged. (allowed: `LeftHanded`, `RightHanded`)
 * - `rotationOrder` — When a value is sent, it replaces the stored rotation order. When omitted or null,
 * unchanged. (allowed: `Y_P_R`, `Y_R_P`, `R_P_Y`, `P_R_Y`)
 *
 * ### Consistency
 *
 * The service does not verify that coordinate-system or orientation-system fields refer to a single coherent
 * definition, or that they remain consistent with existing images. Callers are responsible for ensuring the
 * resulting stored metadata matches their intended definition.
 */
export const patchDataset = <ThrowOnError extends boolean = false>(
  options: Options<PatchDatasetData, ThrowOnError>
): RequestResult<PatchDatasetResponses, PatchDatasetErrors, ThrowOnError> =>
  (options.client ?? client).patch<
    PatchDatasetResponses,
    PatchDatasetErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Trigger a batch ingest operation for all eligible images in a Dataset
 *
 * ### Ingest Dataset
 *
 * This endpoint triggers a batch ingest operation for all eligible images in a dataset.
 * Only images with `AutoIngest=false` and `Status=Downloadable` will be processed.
 *
 * ### Query Parameters
 *
 * - `forceReingest` (optional, default: false): When set to `true`, deletes all existing
 * tilesets before re-ingesting. This is a destructive operation. Also required to re-ingest
 * a dataset that is already ingested or currently being ingested.
 *
 * ### Notes
 *
 * - The ingest operation runs asynchronously and returns a Job.
 * - The response includes a `Location` header with the URL to poll for job status.
 * - Images are grouped by ImageType and processed in batches of up to 1000 images.
 * - The job tracks total images, ingested count, failed count, and lists of failed image IDs.
 * - Child processing jobs are created for each batch of images by type.
 *
 * ### Force Re-Ingest Mode
 *
 * When `forceReingest=true`:
 * 1. Delete stored file data for each existing image tileset
 * 2. Clear tileset metadata from each image
 * 3. Create a DatasetIngest job to regenerate all tiles
 *
 * **Warning**: This is a destructive operation - all existing tilesets will be permanently deleted.
 *
 * ### Tracking the Ingest Job
 *
 * Use the Job URL provided in the `Location` header to monitor the ingest progress:
 * - `GET /jobs/{jobId}` to check the job status
 * - Job `Status` will be one of: `Accepted`, `Running`, `Completed`, or `Failed`
 * - Job `Data` includes:
 * - `TotalImages`: Total number of images to ingest
 * - `IngestedCount`: Number of images successfully ingested
 * - `FailedCount`: Number of images that failed
 * - `FailedImageIds`: List of image IDs that failed
 * - `ProcessingJobIds`: List of child processing job IDs
 * - `IngestJobId` (forceReingest only): ID of the follow-up ingest job
 *
 * ### Use Cases
 *
 * - Batch ingesting a large dataset where AutoIngest was disabled
 * - Re-ingesting images after a processing framework update (use `forceReingest=true`)
 * - Processing images that were uploaded before AutoIngest was enabled
 * - Fixing corrupted tilesets (use `forceReingest=true`)
 */
export const ingestDataset = <ThrowOnError extends boolean = false>(
  options: Options<IngestDatasetData, ThrowOnError>
): RequestResult<IngestDatasetResponses, IngestDatasetErrors, ThrowOnError> =>
  (options.client ?? client).post<
    IngestDatasetResponses,
    IngestDatasetErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}/ingest',
    ...options,
  });

/**
 * Query Datasets with optional filters
 *
 * ### Query Datasets
 *
 * This endpoint allows you to query Datasets with optional filters.
 *
 * ### Query Format
 *
 * The `query` property accepts a JSON object with the following supported formats:
 * - Empty object `{}` - returns all datasets (with pagination)
 * - `{"id": ["guid1", "guid2", ...]}` - filters datasets by their IDs
 *
 * ### Notes
 *
 * - The list is paginated with the optional page parameters.
 * - The list is sorted by creation date in descending order.
 */
export const queryDatasets = <ThrowOnError extends boolean = false>(
  options: Options<QueryDatasetsData, ThrowOnError>
): RequestResult<QueryDatasetsResponses, QueryDatasetsErrors, ThrowOnError> =>
  (options.client ?? client).post<
    QueryDatasetsResponses,
    QueryDatasetsErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/query',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Check the health of the services
 *
 * ### Check Health
 *
 * This endpoint will check the health of the external services and return the status of each service.
 *
 * ### Notes
 *
 * - This endpoint is does not require authentication.
 * - The region parameter should be one of the supported Trimble Connect regions (northAmerica, europe, asia, australia).
 */
export const checkHealth = <ThrowOnError extends boolean = false>(
  options?: Options<CheckHealthData, ThrowOnError>
): RequestResult<CheckHealthResponses, CheckHealthErrors, ThrowOnError> =>
  (options?.client ?? client).get<
    CheckHealthResponses,
    CheckHealthErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/health/{region}',
    ...options,
  });

/**
 * Delete all images under a path prefix
 *
 * ### Delete Folder
 * Deletes all images whose path begins with the provided folder prefix. Large operations run asynchronously and return 202 with a Job.
 */
export const deleteImages = <ThrowOnError extends boolean = false>(
  options: Options<DeleteImagesData, ThrowOnError>
): RequestResult<DeleteImagesResponses, DeleteImagesErrors, ThrowOnError> =>
  (options.client ?? client).delete<
    DeleteImagesResponses,
    DeleteImagesErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}/images',
    ...options,
  });

/**
 * List all Images in a Dataset
 *
 * ### List Images
 *
 * This endpoint lists all images in a Dataset.
 *
 * ### Notes
 *
 * - The list is paginated with the optional page parameters.
 * - The list is sorted by creation date in descending order.
 * - The `Status` property indicates the status of each image (Uploadable or Downloadable).
 * - The UploadUrl and DownloadUrl properties are not returned in the list. Call the **Get Image** endpoint to get these URLs.
 * - The optional path must begin and end with a forward slash, such as `/path/to/folder/`.
 *
 * ### Quaternion Conversion
 *
 * When `RotationMode=quaternion` is specified, the response includes a `QuaternionOrientation` field computed from the image's Euler angles.
 *
 * Optionally, you can specify a target orientation system using `TargetUpAxis`, `TargetForwardAxis`, `TargetHandedness`, and `TargetRotationOrder`.
 * If all four target parameters are provided, the quaternion will be converted from the dataset's orientation system to the specified target system.
 */
export const listImages = <ThrowOnError extends boolean = false>(
  options: Options<ListImagesData, ThrowOnError>
): RequestResult<ListImagesResponses, ListImagesErrors, ThrowOnError> =>
  (options.client ?? client).get<
    ListImagesResponses,
    ListImagesErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}/images',
    ...options,
  });

/**
 * Create an Image
 *
 * ### Create Image
 *
 * This endpoint creates an image.
 *
 * ### Notes
 *
 * - The `Path` property must begin with a forward slash, but not end with a forward slash, such as `/path/to/image.jpg`.
 * - The `Multipart` property determines the format of the upload URL.
 * - The maximum image size is **5TB**.
 * - All uploads require a call to the **Complete Upload** endpoint to finalize the upload.
 * - The upload URL expires after **24 hours**.
 *
 * ### Singlepart Upload
 *
 * If the `Multipart` property is set to false, the UploadUrl property that is returned will be set to a URL that can be used to upload the image in a single request.
 *
 * Maximum size of a single part upload is **5GB**
 *
 * The URL will look something like this: `https://some.host.com/s/{guid}?Policy={policy}&Signature={signature}&Key-Pair-Id={key-pair}`
 *
 * Use the URL to upload the image in a single `PUT` request.
 *
 * Finally, call the **Complete Upload** endpoint to complete the upload, without specifing any `Parts` in the request body.
 *
 * ### Multipart Upload
 *
 * If the `Multipart` property is set to true, the UploadUrl property that is returned will be set to a URL that can be used to upload the image in parts.
 *
 * - The minimum part size for multipart uploads is **5MB**. Except for the last part, which may be smaller.
 * - The maximum part size for multipart uploads is **5GB**.
 * - The maximum number of parts for multipart uploads is **10,000**.
 *
 * The URL will look something like this: `https://some.host.com/s/{guid}?partNumber=*&uploadId={id}&Policy={policy}&Signature={signature}&Key-Pair-Id={key-pair}`
 *
 * There is a query string parameter `partNumber=*` in the upload URL from the previous request. Substitute the `*` value with the number of the current part you are uploading.
 *
 * Separate the image into parts, and upload each to the URL with a `PUT` request and the parameter value substituted. Part numbering starts with `1` (not zero).
 *
 * Your response will include an `ETag` in the header. Keep track of the ETag responses for each part upload. You will need them for the completion call.
 *
 * Finally, call the **Complete Upload** endpoint to complete the upload, providing the `ETag` values and `part number` for each part.
 */
export const createImage = <ThrowOnError extends boolean = false>(
  options: Options<CreateImageData, ThrowOnError>
): RequestResult<CreateImageResponses, CreateImageErrors, ThrowOnError> =>
  (options.client ?? client).post<
    CreateImageResponses,
    CreateImageErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}/images',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Create multiple Images in batch
 *
 * ### Create Multiple Images
 *
 * This endpoint creates multiple images in a single batch request.
 *
 * ### Notes
 *
 * - The maximum number of images per request is **100**.
 * - Images are created in parallel for optimal performance.
 * - Each image follows the same rules as the single **Create Image** endpoint.
 * - The `Path` property for each image must begin with a forward slash, but not end with a forward slash, such as `/path/to/image.jpg`.
 * - The `Multipart` property determines the format of the upload URL for each image.
 * - The maximum image size is **5TB** per image.
 * - All uploads require a call to the **Complete Upload** endpoint to finalize each upload.
 * - Upload URLs expire after **24 hours**.
 *
 * See the **Create Image** endpoint for more information on singlepart and multipart uploads.
 */
export const createImages = <ThrowOnError extends boolean = false>(
  options: Options<CreateImagesData, ThrowOnError>
): RequestResult<CreateImagesResponses, CreateImagesErrors, ThrowOnError> =>
  (options.client ?? client).post<
    CreateImagesResponses,
    CreateImagesErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}/images/bulk',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Delete an Image
 *
 * ### Delete Image
 *
 * This endpoint deletes an image.
 */
export const deleteImage = <ThrowOnError extends boolean = false>(
  options: Options<DeleteImageData, ThrowOnError>
): RequestResult<DeleteImageResponses, DeleteImageErrors, ThrowOnError> =>
  (options.client ?? client).delete<
    DeleteImageResponses,
    DeleteImageErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}/images/{imageId}',
    ...options,
  });

/**
 * Get an Image by id
 *
 * ### Get Image
 *
 * This endpoint gets an image by id.
 *
 * ### Notes
 *
 * - The `Status` property indicates the lifecycle state of the image:
 * - `Uploadable`: Ready for upload, `UploadUrl` is provided
 * - `Processing`: Upload is being finalized, no URLs available (poll GET /images/{id} or follow job)
 * - `Downloadable`: Ready for download, `DownloadUrl` is provided
 * - `Error`: Upload failed
 * - Upload and download URLs expire after **24 hours**.
 * - If the image has a `Tileset`, it will also have its own `Status` following the same values.
 *
 * ### Downloading Images
 *
 * Call `GET` on the `DownloadUrl` to download the image.
 *
 * Range headers are supported for partial downloads and is required for images larger than **20GB**.
 *
 * ### Quaternion Conversion
 *
 * When `RotationMode=quaternion` is specified, the response includes a `QuaternionOrientation` field computed from the image's Euler angles.
 *
 * Optionally, you can specify a target orientation system using `TargetUpAxis`, `TargetForwardAxis`, `TargetHandedness`, and `TargetRotationOrder`.
 * If all four target parameters are provided, the quaternion will be converted from the dataset's orientation system to the specified target system.
 */
export const getImage = <ThrowOnError extends boolean = false>(
  options: Options<GetImageData, ThrowOnError>
): RequestResult<GetImageResponses, GetImageErrors, ThrowOnError> =>
  (options.client ?? client).get<
    GetImageResponses,
    GetImageErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}/images/{imageId}',
    ...options,
  });

/**
 * Complete a multipart Image upload
 *
 * ### Complete Upload
 *
 * This endpoint completes a singlepart or multipart image upload.
 *
 * ### Completion Behavior
 *
 * Upload completion may complete quickly or asynchronously:
 *
 * - **200 OK**: Upload finalized immediately. Image `Status` = `Downloadable`, `DownloadUrl` is available.
 * - **202 Accepted** with `Location` header: Upload is being finalized asynchronously. A Job URL is provided in the `Location` header.
 *
 * ### Tracking Async Completion
 *
 * When a `Location` header is returned (pointing to a Job), you have two options:
 *
 * 1. **Poll the Job**: `GET /jobs/{jobId}` until `Status` = `Completed` or `Failed`
 * 2. **Poll the Image**: `GET /images/{imageId}` until `Status` = `Downloadable` or `Error`
 *
 * Both approaches work independently. The Image `Status` provides the resource state regardless of job tracking.
 *
 * ### Upload Notes
 *
 * - If the image was uploaded in parts, the `Parts` property must be set to an array of `Part` objects.
 * - The `ETag` property must be set to the `ETag` value returned from the upload of each part, and the `PartNumber` property must be set to the corresponding part number.
 * - If the image was uploaded in a single request, the `Parts` property must be empty.
 *
 * See the **Create Image** endpoint for more information on singlepart and multipart uploads.
 */
export const completeUpload = <ThrowOnError extends boolean = false>(
  options: Options<CompleteUploadData, ThrowOnError>
): RequestResult<CompleteUploadResponses, CompleteUploadErrors, ThrowOnError> =>
  (options.client ?? client).post<
    CompleteUploadResponses,
    CompleteUploadErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}/images/{imageId}',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Start the upload of a Tileset for an image
 *
 * ### Create
 *
 * This endpoint starts the Tileset upload process
 *
 * ### Notes
 *
 * The upload is expected to be a single tar archive containing one XYZ tileset following the TMS standard
 *
 * The the tar archive can have any name but structure of the tar archive must as follows:
 *
 * - `/tileset/`
 * - `/tileset/{z}/{x}/{y}.png` - where `z`, `x`, and `y` are the zoom, x, and y coordinates of the tiles in the set.
 * - `/tileset/tilemapresource.xml`
 *
 * #### File upload specifics
 *
 * - The `Multipart` property determines the format of the upload URL.
 * - The maximum image size is **5TB**.
 * - All uploads require a call to the **Complete Upload** endpoint to finalize the upload.
 * - The upload URL expires after **24 hours**.
 *
 * ### Singlepart Upload
 *
 * If the `Multipart` property is set to false, the UploadUrl property that is returned will be set to a URL that can be used to upload the image in a single request.
 *
 * The URL will look something like this: `https://some.host.com/s/{guid}?Policy={policy}&Signature={signature}&Key-Pair-Id={key-pair}`
 *
 * Use the URL to upload the image in a single `PUT` request.
 *
 * Finally, call the **Complete Upload** endpoint to complete the upload, without specifing any `Parts` in the request body.
 *
 * ### Multipart Upload
 *
 * If the `Multipart` property is set to true, the UploadUrl property that is returned will be set to a URL that can be used to upload the image in parts.
 *
 * - The minimum part size for multipart uploads is **5MB**. Except for the last part, which may be smaller.
 * - The maximum part size for multipart uploads is **5GB**.
 * - The maximum number of parts for multipart uploads is **10,000**.
 *
 * The URL will look something like this: `https://some.host.com/s/{guid}?partNumber=*&uploadId={id}&Policy={policy}&Signature={signature}&Key-Pair-Id={key-pair}`
 *
 * There is a query string parameter `partNumber=*` in the upload URL from the previous request. Substitute the `*` value with the number of the current part you are uploading.
 *
 * Divide the tileset .tar archive into parts, and upload each to the URL with a `PUT` request and the parameter value substituted. Part numbering starts with `1` (not zero).
 *
 * Your response will include an `ETag` in the header. Keep track of the ETag responses for each part upload. You will need them for the completion call.
 *
 * Finally, call the **Complete Upload** endpoint to complete the upload, providing the `ETag` values and `part number` for each part.
 */
export const tilesetCreate = <ThrowOnError extends boolean = false>(
  options: Options<TilesetCreateData, ThrowOnError>
): RequestResult<TilesetCreateResponses, TilesetCreateErrors, ThrowOnError> =>
  (options.client ?? client).post<
    TilesetCreateResponses,
    TilesetCreateErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}/images/{imageId}/tileset',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Complete a multipart Tileset upload for an Image
 *
 * ### Complete Upload
 *
 * This endpoint completes a singlepart or multipart tileset upload.
 *
 * ### Completion Behavior
 *
 * Upload completion may complete quickly or asynchronously:
 *
 * - **200 OK**: Tileset finalized immediately. Tileset `Status` = `Downloadable`, `TemplateUrl` is available.
 * - **202 Accepted** with `Location` header: Tileset is being finalized asynchronously. A Job URL is provided in the `Location` header.
 *
 * ### Tracking Async Completion
 *
 * When a `Location` header is returned (pointing to a Job), you have two options:
 *
 * 1. **Poll the Job**: `GET /jobs/{jobId}` until `Status` = `Completed` or `Failed`
 * 2. **Poll the Image**: `GET /images/{imageId}` until `Tileset.Status` = `Downloadable` or `Error`
 *
 * Both approaches work independently. The Tileset `Status` provides the resource state regardless of job tracking.
 *
 * While `Tileset.Status` = `Processing`, the tileset is not yet available and no `TemplateUrl` is provided.
 *
 * ### Upload Notes
 *
 * - If the tileset was uploaded in parts, the `Parts` property must be set to an array of `Part` objects.
 * - The `ETag` property must be set to the `ETag` value returned from the upload of each part, and the `PartNumber` property must be set to the corresponding part number.
 * - If the tileset was uploaded in a single request, the `Parts` property must be empty.
 *
 * See the **Create Tileset** endpoint for more information on singlepart and multipart tileset uploads.
 */
export const tilesetCompleteUpload = <ThrowOnError extends boolean = false>(
  options: Options<TilesetCompleteUploadData, ThrowOnError>
): RequestResult<
  TilesetCompleteUploadResponses,
  TilesetCompleteUploadErrors,
  ThrowOnError
> =>
  (options.client ?? client).post<
    TilesetCompleteUploadResponses,
    TilesetCompleteUploadErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}/images/{imageId}/tileset-complete',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Trigger an ingest operation for an Image
 *
 * ### Ingest Image
 *
 * This endpoint triggers an ingest operation for an image, which creates a processing job
 * to generate tiles or perform other processing tasks based on the image type.
 *
 * ### Notes
 *
 * - The ingest operation runs asynchronously and returns a Job.
 * - The response includes a `Location` header with the URL to poll for job status.
 * - The image must be in a `Downloadable` state before it can be ingested.
 * - Different processing procedures are used based on the image type (Panorama, Planar, Orthophoto).
 * - For **Orthophoto** images, an optional `TilingScheme` query parameter selects the GDAL tiling scheme
 * (e.g. `Raster`, `WebMercatorQuad`, `WorldCRS84Quad`). When omitted, the image's stored
 * `TilingScheme` is used; if that is also missing, ingest defaults to `WebMercatorQuad`.
 * Raster writes to the standard tileset location.
 * WebMercatorQuad and WorldCRS84Quad will write to tilesets with a CRS set to 'epsg:3857' and 'epsg:4326' respectively.
 *
 * ### Tracking the Ingest Job
 *
 * Use the Job URL provided in the `Location` header to monitor the ingest progress:
 * - `GET /jobs/{jobId}` to check the job status
 * - Job `Status` will be one of: `Accepted`, `Running`, `Completed`, or `Failed`
 */
export const ingestImage = <ThrowOnError extends boolean = false>(
  options: Options<IngestImageData, ThrowOnError>
): RequestResult<IngestImageResponses, IngestImageErrors, ThrowOnError> =>
  (options.client ?? client).post<
    IngestImageResponses,
    IngestImageErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}/images/{imageId}/ingest',
    ...options,
  });

/**
 * Get URLs for multiple images by ID
 *
 * ### Get Image URLs
 *
 * This endpoint retrieves upload/download URLs for multiple images in a single request.
 *
 * ### Notes
 *
 * - Provide a list of image IDs in the request body.
 * - Use `GetImageUrls` to fetch image upload/download URLs.
 * - Use `GetTilesetUrls` to fetch tileset upload/template URLs.
 * - URLs are only returned for images that exist and match the requested criteria.
 * - If an image is not found, it will be omitted from the response (no error).
 * - Upload and download URLs expire after **24 hours**.
 *
 * ### Response
 *
 * The response contains an array of image URL objects, each with:
 * - `Id`: The image ID
 * - `Status`: The image status (Uploadable, Processing, Downloadable, Error)
 * - `UploadUrl`/`DownloadUrl`: Image URLs (when `GetImageUrls` is true)
 * - `TilesetStatus`: The tileset status (when image has a tileset)
 * - `TilesetUploadUrl`/`TilesetTemplateUrl`: Tileset URLs (when `GetTilesetUrls` is true)
 */
export const getImageUrls = <ThrowOnError extends boolean = false>(
  options: Options<GetImageUrlsData, ThrowOnError>
): RequestResult<GetImageUrlsResponses, GetImageUrlsErrors, ThrowOnError> =>
  (options.client ?? client).post<
    GetImageUrlsResponses,
    GetImageUrlsErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}/images/urls',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * List all Jobs for a dataset
 *
 * ### List Jobs
 *
 * This endpoint retrieves all jobs for a specific dataset within a tenant.
 *
 * ### Notes
 *
 * - Results are ordered by creation date (newest first)
 * - Use the `status` query parameter to filter by job status
 * - Use the `type` query parameter to filter by job type
 * - Use the `top` query parameter to limit results (default 50, max 100)
 *
 * ### Example Usage
 *
 * - List all jobs: `GET /tenants/{tenantTrn}/datasets/{datasetId}/jobs`
 * - List running jobs: `GET /tenants/{tenantTrn}/datasets/{datasetId}/jobs?status=Running`
 * - List ingest jobs: `GET /tenants/{tenantTrn}/datasets/{datasetId}/jobs?type=DatasetIngest`
 */
export const listJobs = <ThrowOnError extends boolean = false>(
  options: Options<ListJobsData, ThrowOnError>
): RequestResult<ListJobsResponses, ListJobsErrors, ThrowOnError> =>
  (options.client ?? client).get<
    ListJobsResponses,
    ListJobsErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}/jobs',
    ...options,
  });

/**
 * Cancel a Job
 *
 * ### Cancel Job
 *
 * This endpoint attempts to cancel a running or queued job.
 *
 * ### Notes
 *
 * - Only jobs that are in **Accepted** or **Running** status can be cancelled
 * - Jobs that are already **Completed**, **Failed**, or **Cancelled** cannot be cancelled
 * - Jobs that cannot be cancelled will return a 409 Conflict response
 * - The cancellation is asynchronous and may take some time to complete
 * - After cancellation, the job status will change to **Cancelled**
 *
 * ### Response Codes
 *
 * - **204 No Content**: The job cancellation request has been accepted
 * - **404 Not Found**: The job was not found or could not be cancelled
 * - **409 Conflict**: The job type does not support cancellation (e.g., DeleteImages jobs)
 *
 * ### Job Types and Cancellation Support
 *
 * - **Example**: Can be cancelled
 * - **DeleteImages**: Cannot be cancelled (returns 409 Conflict)
 *
 * ### Monitoring Cancellation
 *
 * After requesting cancellation, you can monitor the job status using the **Get Job** endpoint.
 * The job status will eventually change to **Cancelled** when the cancellation is complete.
 */
export const cancelJob = <ThrowOnError extends boolean = false>(
  options: Options<CancelJobData, ThrowOnError>
): RequestResult<CancelJobResponses, CancelJobErrors, ThrowOnError> =>
  (options.client ?? client).delete<
    CancelJobResponses,
    CancelJobErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}/jobs/{jobId}',
    ...options,
  });

/**
 * Get a Job by id
 *
 * ### Get Job
 *
 * This endpoint retrieves a job by its ID within a specific dataset.
 *
 * ### Notes
 *
 * - The `Status` property indicates the current status of the job:
 * - **Accepted**: The job has been accepted and is queued for processing
 * - **Running**: The job is currently being processed
 * - **Completed**: The job has completed successfully
 * - **Failed**: The job failed during processing
 * - **Cancelled**: The job was cancelled before completion
 * - The `Type` property indicates the type of job:
 * - **Example**: Example job type for testing
 * - **DeleteImages**: Job for deleting folders and their contents
 * - The `Message` property contains additional information about the job status or any error messages
 * - The `CreatedBy` property indicates who created the job
 * - The `CreatedAt` property shows when the job was created
 *
 * ### Job Lifecycle
 *
 * Jobs typically progress through the following states:
 * 1. **Accepted** - Job is queued for processing
 * 2. **Running** - Job is actively being processed
 * 3. **Completed** or **Failed** - Job has finished (successfully or with an error)
 *
 * Jobs can also be **Cancelled** at any point before completion.
 */
export const getJob = <ThrowOnError extends boolean = false>(
  options: Options<GetJobData, ThrowOnError>
): RequestResult<GetJobResponses, GetJobErrors, ThrowOnError> =>
  (options.client ?? client).get<GetJobResponses, GetJobErrors, ThrowOnError>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}/jobs/{jobId}',
    ...options,
  });

/**
 * Delete the Trajectory and its files.
 *
 * ### Delete Trajectory
 *
 * Permanently deletes the Trajectory record and all of its files under the dataset's
 * `/trajectory` folder.
 */
export const deleteTrajectory = <ThrowOnError extends boolean = false>(
  options: Options<DeleteTrajectoryData, ThrowOnError>
): RequestResult<
  DeleteTrajectoryResponses,
  DeleteTrajectoryErrors,
  ThrowOnError
> =>
  (options.client ?? client).delete<
    DeleteTrajectoryResponses,
    DeleteTrajectoryErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}/trajectory',
    ...options,
  });

/**
 * Get the Trajectory for a Dataset.
 *
 * ### Get Trajectory
 *
 * Retrieves the Trajectory for a Dataset. This returns the trajectory resource itself:
 * input files (GNSS rover, event) plus the aggregate `status` and `message`. Per-segment
 * processing outputs (SBET / report / image positions and their download URLs) are exposed
 * separately via `GET .../trajectory/results`.
 *
 * ### Notes
 *
 * - Each file in the response includes a `path` (client path) matching what was declared at create time.
 * - When the Trajectory is in `Uploadable` status, upload URLs are included (and refreshed if expired).
 * - `status` reflects the upload lifecycle before processing, and the aggregate per-segment status afterwards.
 * - To retrieve per-segment job ids and output download URLs, call `GET .../trajectory/results`.
 * - Physical storage for inputs remains under `/{datasetId}/trajectory{path}` in the dataset container.
 * - GNSS base and LiDAR files are not supported at this time (not returned on this API).
 */
export const getTrajectory = <ThrowOnError extends boolean = false>(
  options: Options<GetTrajectoryData, ThrowOnError>
): RequestResult<GetTrajectoryResponses, GetTrajectoryErrors, ThrowOnError> =>
  (options.client ?? client).get<
    GetTrajectoryResponses,
    GetTrajectoryErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}/trajectory',
    ...options,
  });

/**
 * Create a Trajectory for a Dataset.
 *
 * ### Create Trajectory
 *
 * Creates a Trajectory under a Dataset. Each Dataset can have at most one Trajectory.
 *
 * ### File storage location
 *
 * Paths in the request body are **client-relative paths** under the dataset's trajectory root.
 * They are not filenames only: you may use nested folders (segments, flights, and so on).
 *
 * Each declared path is stored in the dataset's file container at:
 *
 * `/{datasetId}/trajectory{clientPath}`
 *
 * For example, if `datasetId` is `a1b2c3d4-...` and you declare
 * `gnssRoverPaths: ["/Mission Flight 1/Segment 1/rover01.obs"]`, the blob is stored at:
 *
 * `/a1b2c3d4-.../trajectory/Mission Flight 1/Segment 1/rover01.obs`
 *
 * This is separate from **Images**, which are stored at `/{datasetId}{imagePath}` (no `/trajectory` prefix).
 * Use the same folder naming in trajectory paths as in image paths when you want a parallel layout
 * (for example `/Mission Flight 1/Segment 1/001.JPG` for images and
 * `/Mission Flight 1/Segment 1/rover01.obs` for trajectory inputs under the same segment).
 *
 * ### Path rules
 *
 * - Each path must begin with `/` and must **not** end with `/`.
 * - No backslashes; no `//` in the path.
 * - Paths must be unique within each list (case-insensitive).
 * - `gnssRoverPaths`: required, at least one entry; each file must have a `.obs` extension.
 * - `eventFilePaths`: optional, 0 to N entries; each file must have a `.mrk` extension.
 * - GNSS base and LiDAR files are not supported at this time; if included in the request body, they are ignored.
 *
 * ### Upload workflow
 *
 * 1. Call this endpoint and note each file's `path` and `uploadUrl` in the response.
 * 2. `PUT` file bytes to each upload URL (multipart uploads use `partNumber=*` substitution).
 * 3. Call **Complete Trajectory Upload** with `gnssRoverParts` and optional `eventParts` keyed by the **same paths** declared here.
 */
export const createTrajectory = <ThrowOnError extends boolean = false>(
  options: Options<CreateTrajectoryData, ThrowOnError>
): RequestResult<
  CreateTrajectoryResponses,
  CreateTrajectoryErrors,
  ThrowOnError
> =>
  (options.client ?? client).post<
    CreateTrajectoryResponses,
    CreateTrajectoryErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}/trajectory',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Get the per-segment processing results for a Dataset's Trajectory.
 *
 * ### Get Trajectory Results
 *
 * Returns the trajectory's aggregate `status` and `message` together with the per-segment
 * `results`. Supports the same optional query filters as `GET .../trajectory`:
 *
 * - `pathPrefix`: only the segment with this exact folder prefix (for example `/Mission Flight 1/Segment 1/`).
 * - `status`: only segments in the given status.
 * - `outputTypes`: only segments that produced at least one of the given artifacts (`Sbet`, `Report`,
 * `ImagePosition`, `ApcCorrectedImagePosition`, `ResultZip`); each returned segment is
 * projected down to only the requested artifacts. Repeat the parameter to pass multiple values.
 *
 * ### Notes
 *
 * - `status` is the aggregate over all segments and is computed before filters are applied, so it
 * always reflects the whole trajectory even when `results` is narrowed by a filter.
 * - `results` is empty until processing has been started for at least one segment.
 */
export const getTrajectoryResults = <ThrowOnError extends boolean = false>(
  options: Options<GetTrajectoryResultsData, ThrowOnError>
): RequestResult<
  GetTrajectoryResultsResponses,
  GetTrajectoryResultsErrors,
  ThrowOnError
> =>
  (options.client ?? client).get<
    GetTrajectoryResultsResponses,
    GetTrajectoryResultsErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}/trajectory/results',
    ...options,
  });

/**
 * Complete the Trajectory file uploads.
 *
 * ### Complete Trajectory Upload
 *
 * Finalizes the multipart uploads for the trajectory's files.
 *
 * ### Notes
 *
 * - Call this after uploading all bytes to the upload URLs from **Create Trajectory**.
 * - Dictionary keys must match the **exact paths** returned on create/get (for example `/Mission Flight 1/Segment 1/rover01.obs`).
 * - When multiple files were declared in a segment (several `.obs` or `.mrk`), include one entry per path.
 * - The Trajectory transitions to `UploadComplete` after this call.
 * - This call is **idempotent**: if the Trajectory is already `UploadComplete` (for example the client
 * retried after a network blip), it returns `200 OK` with the current state instead of `409 Conflict`.
 * - GNSS base and LiDAR files are not supported at this time; if included in the request body, they are ignored.
 */
export const completeTrajectoryUpload = <ThrowOnError extends boolean = false>(
  options: Options<CompleteTrajectoryUploadData, ThrowOnError>
): RequestResult<
  CompleteTrajectoryUploadResponses,
  CompleteTrajectoryUploadErrors,
  ThrowOnError
> =>
  (options.client ?? client).post<
    CompleteTrajectoryUploadResponses,
    CompleteTrajectoryUploadErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}/trajectory/complete',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Start Applanix processing for the Trajectory.
 *
 * ### Start Trajectory Processing
 *
 * Submits the Trajectory for Applanix POSPac Cloud processing.
 *
 * ### Segments and fan-out
 *
 * A Trajectory is split into **segments** by the folder path prefix of its GNSS rover files (one
 * segment per flight/session, for example `/Mission Flight 1/Segment 1/`). Applanix cannot bridge
 * time gaps between flights in a single submission, so this endpoint queues **one Applanix job per
 * segment**, which run in parallel. The response (`TrajectoryProcessingDto`) lists one `segments`
 * entry per queued segment, each with its own `processingJobId`.
 *
 * ### Whole-trajectory vs. single-segment
 *
 * - **No `prefix` (whole trajectory):** queues every segment that is not already `ProcessingComplete`.
 * This both starts not-yet-processed segments and retries failed ones, while leaving completed
 * segments untouched.
 * - **`prefix` query parameter (single segment):** queues only the segment with that exact folder
 * prefix (for example `?prefix=/Mission Flight 1/Segment 1/`). This works independently of the
 * other segments' outcomes (for example to start a remaining segment after another has already
 * completed). It returns 409 if that segment is already `ProcessingPending`/`Processing`, and 404
 * if no segment matches the prefix.
 *
 * ### Notes
 *
 * - Allowed starting states: `UploadComplete`, `ProcessingPartiallyComplete`, `ProcessingFailed`,
 * `ProcessingPartiallyFailed`. Starting while still `Uploadable` (uploads not completed) or while
 * processing is in flight returns 409.
 * - The endpoint is mounted only when the Applanix integration is configured (BaseUrl/ClientId/ClientSecret).
 * - On success the Trajectory transitions to `ProcessingPending` and one orchestrator-managed job is
 * created per queued segment.
 * - Poll **Get Trajectory** for the aggregate status (`Processing` -> `ProcessingComplete` /
 * `ProcessingPartiallyComplete` / `ProcessingPartiallyFailed` / `ProcessingFailed`) and
 * **Get Trajectory Results** for per-segment status, job ids, and output download URLs.
 */
export const startTrajectoryProcessing = <ThrowOnError extends boolean = false>(
  options: Options<StartTrajectoryProcessingData, ThrowOnError>
): RequestResult<
  StartTrajectoryProcessingResponses,
  StartTrajectoryProcessingErrors,
  ThrowOnError
> =>
  (options.client ?? client).post<
    StartTrajectoryProcessingResponses,
    StartTrajectoryProcessingErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/datasets/{datasetId}/trajectory/process',
    ...options,
  });
